/**
 * The Community panel — `docs/plans/community.md`, mocks A, B, G, H.
 *
 * **One column, one view at a time**: list → thread → back. It lives in half a
 * dock (`docs/plans/split-view.md`) or a phone's tab, both too narrow for the
 * mock's two rails, so the group becomes a picker and the topics a chip row —
 * the narrow form the mock already drew for mobile.
 *
 * Pushed: `/ws/community` says when a watched group or your bell changed, and
 * the panel re-reads. Focus and every 60 s still refresh, as the fallback.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  communityEnabled,
  decide,
  deletePost,
  fetchDiscover,
  fetchMySpaces,
  fetchNotifications,
  fetchSpace,
  fetchThread,
  fetchThreads,
  joinOpenHouse,
  markNotificationsRead,
  reportPost,
  summariesEnabled,
  summariseThread,
  votePost,
  type DiscoverEntry,
  type Notification,
  type Post,
  type Space,
  type ThreadSort,
} from "../../api/community";
import { useCommunityPush } from "../../api/community-push";
import { canStartThread, nestComments, relTime, runsSpace, type CommentNode } from "../../lib/community";
import { ModerationQueue, SpaceSettings } from "./CommunityAdmin";
import { CommunityComposer, type AttachSources } from "./CommunityComposer";
import {
  Badge,
  Btn,
  CARD,
  EmbedCard,
  ErrorLine,
  META,
  Pill,
  PostText,
  TopicDot,
  type OpenActions,
} from "./CommunityParts";

const REFRESH_MS = 60_000;
const ORG_KEY = "qf.community.org";

type View =
  | { kind: "list" }
  | { kind: "thread"; id: string }
  | { kind: "compose"; share?: boolean }
  | { kind: "settings" }
  | { kind: "queue" }
  | { kind: "discover" }
  | { kind: "bell" };

export interface CommunityPanelProps extends OpenActions {
  attach: AttachSources;
  /** Bumped by "Share to group": open the composer with the active chart on it. */
  shareNonce?: number;
  onClose?: () => void;
}

function remembered(): string | null {
  try {
    return localStorage.getItem(ORG_KEY);
  } catch {
    return null;
  }
}

function remember(org: string): void {
  try {
    localStorage.setItem(ORG_KEY, org);
  } catch {
    /* a private window: the panel just opens on the first group */
  }
}

export function CommunityPanel({ attach, shareNonce, onClose, onOpenSymbol, onOpenLayout }: CommunityPanelProps) {
  const actions = useMemo<OpenActions>(() => ({ onOpenSymbol, onOpenLayout }), [onOpenSymbol, onOpenLayout]);
  const [spaces, setSpaces] = useState<Space[] | null>(null);
  const [unread, setUnread] = useState(0);
  const [org, setOrg] = useState<string | null>(remembered);
  const [space, setSpace] = useState<Space | null>(null);
  const [topic, setTopic] = useState<string | null>(null);
  const [sort, setSort] = useState<ThreadSort>("new");
  const [threads, setThreads] = useState<Post[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [view, setView] = useState<View>({ kind: "list" });
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // ── loading ────────────────────────────────────────────────────────────────
  const loadSpaces = useCallback(async () => {
    try {
      const mine = await fetchMySpaces();
      setSpaces(mine.spaces);
      setUnread(mine.unread);
      setOrg((was) => (was && mine.spaces.some((s) => s.orgKey === was) ? was : mine.spaces[0]?.orgKey ?? null));
    } catch (cause) {
      setSpaces([]);
      setError(cause instanceof Error ? cause.message : "community did not load");
    }
  }, []);

  useEffect(() => {
    if (communityEnabled()) void loadSpaces();
  }, [loadSpaces]);

  useEffect(() => {
    if (!org) {
      setSpace(null);
      return;
    }
    remember(org);
    let live = true;
    fetchSpace(org)
      .then((s) => {
        if (!live) return;
        setSpace(s);
        setError(null);
        // An admin whose space is not on yet lands on the settings that turn it on.
        if (s.role === "admin" && !s.enabled) setView({ kind: "settings" });
      })
      .catch((cause) => live && setError(cause instanceof Error ? cause.message : "this group did not load"));
    return () => {
      live = false;
    };
  }, [org, tick]);

  useEffect(() => {
    if (!org || !space || (!space.enabled && space.role !== "admin")) return;
    let live = true;
    fetchThreads(org, { topic, sort, limit: 30 })
      .then((out) => {
        if (!live) return;
        setThreads(out.threads);
        setHasMore(out.hasMore);
      })
      .catch((cause) => live && setError(cause instanceof Error ? cause.message : "threads did not load"));
    return () => {
      live = false;
    };
  }, [org, space, topic, sort, tick]);

  // Focus and a minute: the two refreshes the owner chose before socket push.
  useEffect(() => {
    const bump = () => {
      setTick((n) => n + 1);
      void loadSpaces();
    };
    const timer = window.setInterval(bump, REFRESH_MS);
    window.addEventListener("focus", bump);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", bump);
    };
  }, [loadSpaces]);

  // Push (`/ws/community`): a change in a watched group re-reads it — the list,
  // the open thread and the queue counts all follow `tick` — and a bell change
  // re-reads the count. The 60 s refresh above stays as the fallback.
  const watched = useMemo(() => (spaces ?? []).map((s) => s.orgKey), [spaces]);
  const orgRef = useRef(org);
  orgRef.current = org;
  useCommunityPush(watched, {
    onChanged: (orgs) => {
      if (orgRef.current && orgs.includes(orgRef.current)) setTick((n) => n + 1);
    },
    onBell: () => void loadSpaces(),
  });

  // "Share to group" from a chart.
  const lastShare = useRef(shareNonce);
  useEffect(() => {
    if (shareNonce !== undefined && shareNonce !== lastShare.current) {
      lastShare.current = shareNonce;
      setView({ kind: "compose", share: true });
    }
  }, [shareNonce]);

  const loadMore = async () => {
    if (!org) return;
    const out = await fetchThreads(org, { topic, sort, offset: threads.length, limit: 30 });
    setThreads((was) => [...was, ...out.threads]);
    setHasMore(out.hasMore);
  };

  if (!communityEnabled()) {
    return <Shell onClose={onClose}><div style={META}>Community needs a server, and none is configured.</div></Shell>;
  }

  const role = space?.role;
  const topics = (space?.topics ?? []).filter((t) => !t.archived);
  const current = topics.find((t) => t.id === topic);
  const mayPost = topics.some((t) => canStartThread(role, t));

  // ── header ────────────────────────────────────────────────────────────────
  const header = (
    <div style={HEADER}>
      <b style={{ fontSize: 13 }}>💬</b>
      {spaces && spaces.length > 0 ? (
        <select
          value={org ?? ""}
          onChange={(e) => {
            setOrg(e.target.value);
            setTopic(null);
            setView({ kind: "list" });
          }}
          style={GROUP_PICK}
          aria-label="Group"
        >
          {spaces.map((s) => (
            <option key={s.orgKey} value={s.orgKey}>
              {s.name}
              {s.role === "visitor" ? " · Open House" : ""}
              {!s.enabled ? " (off)" : ""}
            </option>
          ))}
        </select>
      ) : (
        <b style={{ fontSize: 13 }}>Community</b>
      )}
      {role === "visitor" && <Badge kind="visitor" />}
      <span style={{ flex: 1 }} />
      <IconBtn title="Discover Open Houses" on={view.kind === "discover"} onClick={() => setView(view.kind === "discover" ? { kind: "list" } : { kind: "discover" })}>🧭</IconBtn>
      <IconBtn title={`Notifications${unread ? ` (${unread} unread)` : ""}`} on={view.kind === "bell"} onClick={() => setView(view.kind === "bell" ? { kind: "list" } : { kind: "bell" })}>
        🔔{unread > 0 && <span style={COUNT}>{unread}</span>}
      </IconBtn>
      {runsSpace(role) && (
        <IconBtn title="Approvals and reports" on={view.kind === "queue"} onClick={() => setView(view.kind === "queue" ? { kind: "list" } : { kind: "queue" })}>
          🛡{(space?.queue?.approvals ?? 0) + (space?.queue?.reports ?? 0) > 0 && (
            <span style={COUNT}>{(space?.queue?.approvals ?? 0) + (space?.queue?.reports ?? 0)}</span>
          )}
        </IconBtn>
      )}
      {runsSpace(role) && (
        <IconBtn title="Community settings" on={view.kind === "settings"} onClick={() => setView(view.kind === "settings" ? { kind: "list" } : { kind: "settings" })}>⚙</IconBtn>
      )}
      {onClose && <IconBtn title="Close" onClick={onClose}>✕</IconBtn>}
    </div>
  );

  // ── views ─────────────────────────────────────────────────────────────────
  let body: React.ReactNode;
  if (spaces === null) {
    body = <div style={META}>Loading…</div>;
  } else if (view.kind === "discover") {
    body = (
      <Discover
        onOpen={async (key) => {
          await loadSpaces();
          setOrg(key);
          setTopic(null);
          setView({ kind: "list" });
        }}
      />
    );
  } else if (view.kind === "bell") {
    body = (
      <Bell
        onSeen={() => setUnread(0)}
        onOpen={(n) => {
          if (n.orgKey !== org) setOrg(n.orgKey);
          setView(n.threadId ? { kind: "thread", id: n.threadId } : n.kind === "pending" ? { kind: "queue" } : { kind: "list" });
        }}
      />
    );
  } else if (spaces.length === 0 || !org || !space) {
    body = (
      <div style={{ ...META, padding: 8 }}>
        {spaces.length === 0
          ? "None of your groups has a community yet. A Group Admin turns one on; meanwhile, Open Houses are public areas any app user can join."
          : "Loading…"}
        <div style={{ marginTop: 8 }}>
          <Btn onClick={() => setView({ kind: "discover" })}>🧭 Discover Open Houses</Btn>
        </div>
      </div>
    );
  } else if (view.kind === "settings" && runsSpace(role)) {
    body = (
      <SpaceSettings
        space={space}
        onChanged={(s) => {
          setSpace((was) => ({ ...was, ...s }));
          // A save that worked supersedes an error from before it, and the group
          // picker's "(off)" must follow the switch — it is read from `spaces`.
          setError(null);
          void loadSpaces();
          setTick((n) => n + 1);
        }}
      />
    );
  } else if (view.kind === "queue" && runsSpace(role)) {
    body = (
      <ModerationQueue
        orgKey={org}
        actions={actions}
        onOpenThread={(id) => setView({ kind: "thread", id })}
        onCounts={() => setTick((n) => n + 1)}
      />
    );
  } else if (view.kind === "compose") {
    body = (
      <CommunityComposer
        orgKey={org}
        role={role!}
        topics={topics}
        initialTopicId={topic}
        attach={attach}
        autoAttachChart={view.share}
        onCancel={() => setView({ kind: "list" })}
        onDone={(post) => {
          setTick((n) => n + 1);
          setView({ kind: "thread", id: post.id });
        }}
      />
    );
  } else if (view.kind === "thread") {
    body = (
      <ThreadView
        orgKey={org}
        threadId={view.id}
        space={space}
        attach={attach}
        actions={actions}
        tick={tick}
        onBack={() => {
          setTick((n) => n + 1);
          setView({ kind: "list" });
        }}
      />
    );
  } else {
    body = (
      <>
        <div style={{ display: "flex", gap: 5, overflowX: "auto", paddingBottom: 4 }}>
          <Pill on={topic === null} onClick={() => setTopic(null)}>All</Pill>
          {topics.map((t) => (
            <Pill key={t.id} on={topic === t.id} onClick={() => setTopic(t.id)}>
              <span style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
                <TopicDot colour={t.colour} />
                {t.name}
                {t.postPolicy === "hosts" ? " 🔒" : ""}
              </span>
            </Pill>
          ))}
        </div>
        <div style={{ display: "flex", gap: 5, alignItems: "center", margin: "4px 0 6px" }}>
          {(["new", "hot", "top"] as const).map((s) => (
            <Pill key={s} on={sort === s} onClick={() => setSort(s)}>
              {s === "top" ? "Top · week" : s[0]!.toUpperCase() + s.slice(1)}
            </Pill>
          ))}
          <span style={{ flex: 1 }} />
          {mayPost && <Btn primary small onClick={() => setView({ kind: "compose" })}>+ New post</Btn>}
        </div>
        {current?.description && <div style={{ ...META, marginBottom: 6 }}>{current.description}</div>}
        {!space.enabled && <div style={{ ...META, color: "var(--qf-warn)" }}>Community is off — only you can see this. Turn it on in ⚙.</div>}
        {threads.length === 0 ? (
          <div style={{ ...META, padding: "12px 0" }}>No threads here yet.</div>
        ) : (
          threads.map((t) => (
            <ThreadRow
              key={t.id}
              post={t}
              topics={topics}
              onOpen={() => setView({ kind: "thread", id: t.id })}
              onVote={async () => {
                const out = await votePost(org, t.id);
                setThreads((was) => was.map((x) => (x.id === t.id ? { ...x, score: out.score, voted: out.voted } : x)));
              }}
            />
          ))
        )}
        {hasMore && <Btn small onClick={() => void loadMore()}>More</Btn>}
        {space.rules && (
          <details style={{ marginTop: 10, ...META }}>
            <summary>Rules</summary>
            <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{space.rules}</div>
          </details>
        )}
        <div style={{ ...META, marginTop: 10, fontSize: 10.5 }}>
          {space.openHouse ? "Open House topics are readable by any app user; their posts are checked by hosts. " : "Members only. "}
          Nothing here is financial advice.
        </div>
      </>
    );
  }

  return (
    <Shell onClose={undefined}>
      {header}
      <div style={{ padding: "8px 10px", overflowY: "auto", flex: 1, minHeight: 0 }}>
        <ErrorLine error={error} />
        {body}
      </div>
    </Shell>
  );
}

function Shell({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) {
  return (
    <div
      data-tour="community"
      style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, background: "var(--qf-background)", color: "var(--qf-text)" }}
    >
      {onClose && <div style={HEADER}><span style={{ flex: 1 }} /><IconBtn title="Close" onClick={onClose}>✕</IconBtn></div>}
      {children}
    </div>
  );
}

function IconBtn({ children, title, onClick, on }: { children: React.ReactNode; title: string; onClick: () => void; on?: boolean }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={on}
      onClick={onClick}
      style={{
        position: "relative",
        border: `1px solid ${on ? "var(--qf-accent)" : "transparent"}`,
        background: on ? "var(--qf-selection)" : "none",
        color: "var(--qf-text-secondary)",
        borderRadius: 4,
        padding: "1px 5px",
        cursor: "pointer",
        fontSize: 13,
      }}
    >
      {children}
    </button>
  );
}

// ── rows ─────────────────────────────────────────────────────────────────────

function ThreadRow({
  post,
  topics,
  onOpen,
  onVote,
}: {
  post: Post;
  topics: { id: string; name: string; colour: string }[];
  onOpen: () => void;
  onVote: () => void;
}) {
  const topic = topics.find((t) => t.id === post.topicId);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "34px 1fr", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--qf-border)" }}>
      <VoteBox score={post.score} voted={post.voted} disabled={post.status !== "published"} onVote={onVote} />
      <div style={{ minWidth: 0, cursor: "pointer" }} onClick={onOpen}>
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>
          {post.pinned && <Badge kind="pinned" />}
          {post.pinned && post.pinnedUntil && <span style={{ ...META, marginRight: 5 }}>until {pinDate(post.pinnedUntil)}</span>}
          {post.status === "pending" && <Badge kind="pending" />}
          {post.status === "rejected" && <span style={{ ...META, color: "var(--qf-negative)" }}>Not approved · </span>}
          {post.title}
        </div>
        <div style={META}>
          {topic && (
            <span style={{ display: "inline-flex", gap: 4, alignItems: "center", marginRight: 6 }}>
              <TopicDot colour={topic.colour} />
              {topic.name}
            </span>
          )}
          {post.author}
          {post.authorBadge && post.authorBadge !== "visitor" ? ` (${post.authorBadge === "admin" ? "Group Admin" : "Host"})` : ""}
          {post.authorBadge === "visitor" ? " · visitor" : ""} · {relTime(post.createdAt)} · {post.commentCount} 💬
          {post.locked ? " · 🔒" : ""}
          {post.embeds.length > 0 ? ` · ${post.embeds.map((e) => ({ chart: "📷", layout: "▦", screen: "▦", plan: "🎯", tracklist: "★" })[e.kind]).join(" ")}` : ""}
        </div>
      </div>
    </div>
  );
}

function VoteBox({ score, voted, disabled, onVote }: { score: number; voted: boolean; disabled?: boolean; onVote: () => void }) {
  return (
    <div style={{ textAlign: "center", lineHeight: 1.1 }}>
      <button
        type="button"
        onClick={onVote}
        disabled={disabled}
        aria-pressed={voted}
        title={voted ? "Remove your up-vote" : "Up-vote"}
        style={{ border: 0, background: "none", cursor: disabled ? "default" : "pointer", color: voted ? "var(--qf-positive)" : "var(--qf-text-secondary)", fontSize: 13 }}
      >
        ▲
      </button>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{disabled ? "–" : score}</div>
    </div>
  );
}

// ── a thread ─────────────────────────────────────────────────────────────────

function ThreadView({
  orgKey,
  threadId,
  space,
  attach,
  actions,
  tick,
  onBack,
}: {
  orgKey: string;
  threadId: string;
  space: Space;
  attach: AttachSources;
  actions: OpenActions;
  tick: number;
  onBack: () => void;
}) {
  const [thread, setThread] = useState<Post | null>(null);
  const [comments, setComments] = useState<Post[]>([]);
  const [replying, setReplying] = useState<string | null>(null);
  const [editing, setEditing] = useState<Post | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [summarising, setSummarising] = useState(false);
  const [n, setN] = useState(0);
  const role = space.role;

  useEffect(() => {
    let live = true;
    fetchThread(orgKey, threadId)
      .then((out) => {
        if (!live) return;
        setThread(out.thread);
        setComments(out.comments);
        setError(null);
      })
      .catch((cause) => live && setError(cause instanceof Error ? cause.message : "the thread did not load"));
    return () => {
      live = false;
    };
  }, [orgKey, threadId, tick, n]);

  const reload = () => setN((x) => x + 1);
  const guard = (fn: () => Promise<unknown>) => async () => {
    setError(null);
    try {
      await fn();
      reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "that did not work");
    }
  };

  if (!thread) {
    return (
      <div>
        <Btn small onClick={onBack}>← Back</Btn>
        <ErrorLine error={error} />
      </div>
    );
  }

  const tree = nestComments(thread.id, comments);
  const topicReplies = (space.topics ?? []).find((t) => t.id === thread.topicId)?.replyPolicy ?? "everyone";
  const repliesAllowed = topicReplies === "everyone" || (topicReplies === "hosts" && runsSpace(role));
  const canReply =
    thread.status === "published" && role !== "outsider" && repliesAllowed && (!thread.locked || runsSpace(role));

  const actionsFor = (p: Post) => (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", ...META, marginTop: 3 }}>
      {canReply && p.depth < 2 && p.status === "published" && (
        <LinkBtn onClick={() => setReplying(replying === p.id ? null : p.id)}>Reply</LinkBtn>
      )}
      {p.isMine && p.status !== "removed" && <LinkBtn onClick={() => setEditing(p)}>Edit</LinkBtn>}
      {p.isMine && p.status !== "removed" && (
        <LinkBtn onClick={guard(() => (window.confirm("Delete this post?") ? deletePost(orgKey, p.id) : Promise.resolve()))}>Delete</LinkBtn>
      )}
      {!p.isMine && role !== "outsider" && p.status === "published" && (
        <LinkBtn
          onClick={guard(async () => {
            const reason = window.prompt("Report as: spam, promotion, misleading, abuse or other", "spam");
            if (reason) await reportPost(orgKey, p.id, reason.trim().toLowerCase());
          })}
        >
          Report
        </LinkBtn>
      )}
      {runsSpace(role) && p.status === "pending" && (
        <>
          <LinkBtn onClick={guard(() => decide(orgKey, p.id, "approve"))}>Approve</LinkBtn>
          <LinkBtn onClick={guard(() => decide(orgKey, p.id, "reject", window.prompt("Reason (the author sees it)") ?? ""))}>Reject</LinkBtn>
        </>
      )}
      {runsSpace(role) && p.status === "published" && !p.isMine && (
        <LinkBtn onClick={guard(() => (window.confirm("Remove this post?") ? decide(orgKey, p.id, "remove") : Promise.resolve()))}>Remove</LinkBtn>
      )}
    </div>
  );

  const bodyOf = (p: Post) =>
    p.status === "removed" ? (
      <div style={{ ...META, fontStyle: "italic" }}>[removed by a host]</div>
    ) : (
      <>
        {p.body && <PostText text={p.body} onSymbol={actions.onOpenSymbol} />}
        {p.embeds.map((e, i) => <EmbedCard key={i} embed={e} author={p.author} actions={actions} />)}
        {p.isMine && p.pendingEdit && <div style={META}><Badge kind="edit" />readers still see the text above until a host approves your edit</div>}
        {p.status === "rejected" && <div style={{ ...META, color: "var(--qf-negative)" }}>Not approved{p.rejectReason ? `: ${p.rejectReason}` : ""}. Edit it to send it back.</div>}
      </>
    );

  const renderNode = (node: CommentNode) => (
    <div key={node.post.id} style={{ borderLeft: "2px solid var(--qf-border)", padding: "6px 0 2px 10px", marginTop: 6 }}>
      <div style={META}>
        <b style={{ color: "var(--qf-text)" }}>{node.post.author}</b>
        {node.post.authorBadge === "admin" ? " · Group Admin" : node.post.authorBadge === "host" ? " · Host" : node.post.authorBadge === "visitor" ? " · visitor" : ""}
        {node.post.isMine ? "" : ""} · {relTime(node.post.createdAt)}
        {node.post.editedAt ? " · edited" : ""} · ▲{" "}
        <button type="button" style={{ border: 0, background: "none", color: node.post.voted ? "var(--qf-positive)" : "inherit", cursor: "pointer", padding: 0 }} onClick={guard(() => votePost(orgKey, node.post.id))}>
          {node.post.score}
        </button>
        {node.post.status === "pending" && <> <Badge kind="pending" /></>}
      </div>
      {editing?.id === node.post.id ? (
        <CommunityComposer orgKey={orgKey} role={role} topics={space.topics ?? []} edit={node.post} attach={attach} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); reload(); }} />
      ) : (
        bodyOf(node.post)
      )}
      {actionsFor(node.post)}
      {replying === node.post.id && (
        <CommunityComposer orgKey={orgKey} role={role} topics={space.topics ?? []} parentId={node.post.id} attach={attach} onCancel={() => setReplying(null)} onDone={() => { setReplying(null); reload(); }} />
      )}
      {node.children.map(renderNode)}
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
        <Btn small onClick={onBack}>← Back</Btn>
        <span style={{ flex: 1 }} />
        {runsSpace(role) && thread.status === "published" && (
          <>
            {thread.pinned ? (
              <Btn small onClick={guard(() => decide(orgKey, thread.id, "unpin"))}>Unpin</Btn>
            ) : (
              <PinMenu onPin={(days) => void guard(() => decide(orgKey, thread.id, "pin", "", days))()} />
            )}
            <Btn small onClick={guard(() => decide(orgKey, thread.id, thread.locked ? "unlock" : "lock"))}>{thread.locked ? "Unlock" : "Lock"}</Btn>
          </>
        )}
        {summariesEnabled() && thread.status === "published" && (
          <Btn
            small
            disabled={summarising}
            title="QueVee condenses the thread you can see"
            onClick={async () => {
              setSummarising(true);
              const out = await summariseThread(orgKey, thread.id).catch(() => ({ ok: false as const, error: { message: "Summaries are temporarily unavailable." } }));
              setSummary(out.ok ? out.summary : out.error.message);
              setSummarising(false);
            }}
          >
            {summarising ? "Summarising…" : "✨ Summarise"}
          </Btn>
        )}
      </div>
      <ErrorLine error={error} />
      {summary && (
        <div style={{ ...CARD, whiteSpace: "pre-wrap", fontSize: 12.5 }}>
          <div style={{ ...META, marginBottom: 4 }}>QueVee's summary — it can be wrong; read the thread for anything you act on.</div>
          {summary}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "34px 1fr", gap: 8 }}>
        <VoteBox score={thread.score} voted={thread.voted} disabled={thread.status !== "published"} onVote={guard(() => votePost(orgKey, thread.id))} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {thread.pinned && <Badge kind="pinned" />}
            {thread.pinned && (
              <span style={{ ...META, fontWeight: 400, marginRight: 5 }}>
                {thread.pinnedUntil ? `until ${pinDate(thread.pinnedUntil)}` : "no expiry"}
              </span>
            )}
            {thread.locked && <Badge kind="locked" />}
            {thread.status === "pending" && <Badge kind="pending" />}
            {thread.title}
          </div>
          <div style={META}>
            {thread.author}
            {thread.authorBadge === "admin" ? " · Group Admin" : thread.authorBadge === "host" ? " · Host" : thread.authorBadge === "visitor" ? " · visitor" : ""} ·{" "}
            {relTime(thread.createdAt)}
            {thread.editedAt ? " · edited" : ""}
            {thread.status === "pending" && thread.isMine ? " · only you and the group's hosts can see this" : ""}
          </div>
          {editing?.id === thread.id ? (
            <CommunityComposer orgKey={orgKey} role={role} topics={space.topics ?? []} edit={thread} attach={attach} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); reload(); }} />
          ) : (
            bodyOf(thread)
          )}
          {actionsFor(thread)}
          {replying === thread.id && (
            <CommunityComposer orgKey={orgKey} role={role} topics={space.topics ?? []} parentId={thread.id} attach={attach} onCancel={() => setReplying(null)} onDone={() => { setReplying(null); reload(); }} />
          )}
        </div>
      </div>
      <div style={{ fontWeight: 600, fontSize: 12.5, margin: "12px 0 2px" }}>
        {thread.commentCount} comment{thread.commentCount === 1 ? "" : "s"}
      </div>
      {tree.map(renderNode)}
      {canReply && replying !== thread.id && (
        <div style={{ marginTop: 10 }}>
          <Btn small onClick={() => setReplying(thread.id)}>Add a comment</Btn>
        </div>
      )}
      {thread.locked && !runsSpace(role) && <div style={{ ...META, marginTop: 8 }}>🔒 This thread is locked.</div>}
      {topicReplies === "off" && <div style={{ ...META, marginTop: 8 }}>Replies are off in this topic.</div>}
      {topicReplies === "hosts" && !runsSpace(role) && <div style={{ ...META, marginTop: 8 }}>Only hosts reply in this topic.</div>}
    </div>
  );
}

/** How long a pin lasts — the choices offered; the server accepts 1 to 365 days. */
const PIN_CHOICES: { label: string; days?: number }[] = [
  { label: "No expiry" },
  { label: "1 day", days: 1 },
  { label: "3 days", days: 3 },
  { label: "7 days", days: 7 },
  { label: "14 days", days: 14 },
  { label: "30 days", days: 30 },
];

function pinDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** Pin, then how long for. A pin with days unpins itself when they run out. */
function PinMenu({ onPin }: { onPin: (days?: number) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative" }}>
      <Btn small onClick={() => setOpen((o) => !o)} title="Pin to the top of the topic">Pin ▾</Btn>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            zIndex: 20,
            marginTop: 2,
            background: "var(--qf-surface)",
            border: "1px solid var(--qf-border)",
            borderRadius: 5,
            padding: 3,
            minWidth: 110,
          }}
        >
          {PIN_CHOICES.map((c) => (
            <button
              key={c.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPin(c.days);
              }}
              style={{ display: "block", width: "100%", textAlign: "left", border: 0, background: "none", color: "var(--qf-text)", padding: "3px 8px", fontSize: 12, cursor: "pointer" }}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function LinkBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ border: 0, background: "none", padding: 0, color: "var(--qf-text-secondary)", cursor: "pointer", font: "inherit" }}>
      {children}
    </button>
  );
}

// ── Discover and the bell ────────────────────────────────────────────────────

function Discover({ onOpen }: { onOpen: (orgKey: string) => void | Promise<void> }) {
  const [entries, setEntries] = useState<DiscoverEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    fetchDiscover()
      .then(setEntries)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Discover did not load"));
  }, []);
  useEffect(load, [load]);
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Open Houses</div>
      <div style={META}>Public areas of groups in this app. Joining is free and is not membership — the group's own area stays members-only.</div>
      <ErrorLine error={error} />
      {entries === null ? (
        <div style={META}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ ...META, marginTop: 8 }}>No group has an Open House yet.</div>
      ) : (
        entries.map((e) => (
          <div key={e.orgKey} style={CARD}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <b>{e.name}</b>
              <span style={META}>{e.members} members · {e.visitors} visitors</span>
              <span style={{ flex: 1 }} />
              {e.isMember ? (
                <Btn small onClick={() => void onOpen(e.orgKey)}>Open — you're a member</Btn>
              ) : e.joined ? (
                <>
                  <Btn small onClick={() => void onOpen(e.orgKey)}>Open</Btn>
                  <Btn small onClick={async () => { await joinOpenHouse(e.orgKey, false); load(); }}>Leave</Btn>
                </>
              ) : (
                <Btn small primary onClick={async () => { await joinOpenHouse(e.orgKey, true); await onOpen(e.orgKey); }}>Join Open House</Btn>
              )}
            </div>
            {e.description && <div style={{ ...META, marginTop: 4 }}>{e.description}</div>}
            {e.openTopics.length > 0 && <div style={{ ...META, marginTop: 2 }}>Open topics: {e.openTopics.join(" · ")}</div>}
          </div>
        ))
      )}
    </div>
  );
}

function Bell({ onOpen, onSeen }: { onOpen: (n: Notification) => void; onSeen: () => void }) {
  const [items, setItems] = useState<Notification[] | null>(null);
  useEffect(() => {
    let live = true;
    fetchNotifications()
      .then(async (out) => {
        if (!live) return;
        setItems(out.items);
        if (out.unread > 0) {
          await markNotificationsRead();
          onSeen();
        }
      })
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
  }, [onSeen]);
  if (items === null) return <div style={META}>Loading…</div>;
  if (items.length === 0) return <div style={META}>Nothing yet. Replies to you, and decisions on your posts, land here.</div>;
  return (
    <div>
      {items.map((n) => (
        <div
          key={n.id}
          onClick={() => onOpen(n)}
          style={{ padding: "6px 4px", borderBottom: "1px solid var(--qf-border)", cursor: "pointer", fontWeight: n.read ? 400 : 600, fontSize: 12.5 }}
        >
          <div>{n.text}</div>
          <div style={META}>{n.group} · {relTime(n.createdAt)}</div>
        </div>
      ))}
    </div>
  );
}

const HEADER: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  flex: "none",
};
const GROUP_PICK: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  maxWidth: 180,
  color: "var(--qf-text)",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "1px 4px",
};
const COUNT: React.CSSProperties = {
  position: "absolute",
  top: -5,
  right: -6,
  minWidth: 14,
  height: 14,
  padding: "0 3px",
  borderRadius: 7,
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontSize: 9.5,
  fontWeight: 700,
  lineHeight: "14px",
};
